import { distanceUnitByUnitType } from '@racemap/utilities/functions/units';
import { clamp } from '@racemap/utilities/math';
import type { UnitType } from '@racemap/utilities/types/units';
import { type JSX, memo, useMemo } from 'react';
import { FONT_FAMILY, FONT_SIZE, FONT_STYLE } from 'src/lib/player/constants';
import { getStarterInfos } from 'src/lib/player/statistics';
import { type Point, type StarterType, type State, useStore, type XY } from 'src/lib/player/store';
import {
  distanceUnitToMeterFactor,
  formatDistance,
  formatTimeDuration,
  meterToDistanceUnitFactor,
  singleMemoize,
} from 'src/lib/player/utils';
import { binarySearchIndexHi, binarySearchIndexLo } from 'src/lib/player/utils/binarySearch';
import { findGridLinesDistance, findGridLinesTime } from './gridlines';
import SVGChart, { type Bounds } from './SVGChart';

function resample<T>(array: Array<T>, extractor: (value: T) => XY, count: number): Array<XY> {
  const realCount = Math.min(count, array.length);
  if (realCount < 3) {
    return array.map(extractor);
  }

  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  for (const item of array) {
    const itemXY = extractor(item);
    minX = Math.min(minX, itemXY.x);
    maxX = Math.max(maxX, itemXY.x);
  }

  const stride = (maxX - minX) / realCount;

  const output = [];
  let i = 0;
  let itemXY = extractor(array[i]);
  for (let x = minX; x <= maxX; x += stride) {
    while (itemXY.x < x) {
      i++;
      itemXY = extractor(array[i]);
    }
    output.push(itemXY);
  }
  return output;
}

function findCurrentOffset(points: Array<XY>, time: number): XY | null {
  const loIdx = binarySearchIndexLo(points, (points, i) => time - points[i].x);
  const hiIdx = binarySearchIndexHi(points, (points, i) => time - points[i].x);
  const loValid = loIdx >= 0 && loIdx < points.length;
  const hiValid = hiIdx >= 0 && hiIdx < points.length;
  if (loValid) {
    if (hiValid) {
      const lo = points[loIdx];
      const hi = points[hiIdx];
      const fraction = (time - lo.x) / (hi.x - lo.x);
      return { x: time, y: lo.y * (1 - fraction) + hi.y * fraction };
    }
    return points[loIdx];
  }
  if (hiValid) {
    return points[hiIdx];
  }

  return null;
}

type FlowChartProps = {
  height: number;
  width: number;
};

const FlowChartStarter = memo(function FlowChartStarter({
  points,
  starter,
  minX,
  maxX,
  minY,
  maxY,
  bounds,
  time,
  unitType,
}: {
  points: Array<Point>;
  starter: StarterType;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  bounds: Bounds;
  time: number;
  unitType: UnitType;
}): JSX.Element {
  const resampledPoints = useMemo(
    () => resample(points, (p) => ({ x: p.time, y: p.offset }), 400),
    [points],
  );
  const svgPath = resampledPoints.map((point) => [
    bounds.paddingLeft + ((point.x - minX) / (maxX - minX)) * bounds.innerWidth,
    bounds.paddingTop + (1 - (point.y - minY) / (maxY - minY)) * bounds.innerHeight,
  ]);

  const currentLine = (() => {
    const currentXY = findCurrentOffset(resampledPoints, time);
    if (currentXY == null) return null;
    const currentX =
      bounds.paddingLeft + ((currentXY.x - minX) / (maxX - minX)) * bounds.innerWidth;
    const currentY =
      bounds.paddingTop + (1 - (currentXY.y - minY) / (maxY - minY)) * bounds.innerHeight;
    const distanceLabel = formatDistance(currentXY.y, unitType);
    const isLeft = time > minX + (maxX - minX) / 2;
    return isLeft ? (
      <>
        <text
          className="notranslate"
          x={bounds.paddingLeft}
          y={currentY - 4}
          style={{
            fontWeight: FONT_STYLE,
            fontFamily: FONT_FAMILY,
            fontSize: FONT_SIZE,
            textAlign: 'left',
            textAnchor: 'start',
            fill: '#ffffff',
          }}
        >
          {starter.startNumber} ({distanceLabel})
        </text>
        <line
          x1={bounds.paddingLeft}
          x2={currentX}
          y1={currentY}
          y2={currentY}
          stroke={starter.markerColor.hex}
          strokeWidth={2}
        />
      </>
    ) : (
      <>
        <text
          className="notranslate"
          x={bounds.paddingLeft + bounds.innerWidth}
          y={currentY - 4}
          style={{
            fontWeight: FONT_STYLE,
            fontFamily: FONT_FAMILY,
            fontSize: FONT_SIZE,
            textAlign: 'right',
            textAnchor: 'end',
            fill: '#ffffff',
          }}
        >
          {starter.startNumber} ({distanceLabel})
        </text>
        <line
          x1={bounds.paddingLeft + bounds.innerWidth}
          x2={currentX}
          y1={currentY}
          y2={currentY}
          stroke={starter.markerColor.hex}
          strokeWidth={2}
        />
      </>
    );
  })();

  return (
    <>
      {currentLine}
      <path
        d={[
          `M ${svgPath[0][0]},${svgPath[0][1]}`,
          ...svgPath.map((svgPathItem) => `L ${svgPathItem[0]},${svgPathItem[1]}`),
        ].join(' ')}
        fill="transparent"
        stroke="#ffffff"
        strokeWidth={1}
      />
    </>
  );
});

// Memoized at module scope: zustand v5 passes the selector result straight to
// useSyncExternalStore, so a new reference per call re-renders in a loop.
const starterInfosSelector = singleMemoize((state: State) =>
  state.timer.selected
    .flatMap((selectedId) => getStarterInfos(state, selectedId))
    .filter(
      (starterInfo) =>
        starterInfo.points.length > 0 &&
        starterInfo.starter.eventIndex === state.currentEventIndices[0],
    ),
);

export default function FlowChart({ width, height }: FlowChartProps): JSX.Element {
  const starterInfos = useStore(starterInfosSelector);
  const unitType = useStore((state) => state.options.unitType);
  const time = useStore((state) => state.timer.time);

  let minX = Number.POSITIVE_INFINITY;
  let maxX = 0;
  const minY = 0;
  let maxY = 0;
  for (const starterInfo of starterInfos) {
    for (const point of starterInfo.points) {
      minX = Math.min(minX, point.time);
      maxX = Math.max(maxX, point.time);
      // minY = Math.min(minY, point.offset);
      maxY = Math.max(maxY, point.offset);
    }
  }
  const xAxis = {
    min: minX,
    max: maxX,
    ticks: findGridLinesTime(0, maxX - minX, clamp(5, Math.round(width / 100), 10)).map(
      (value: number): [number, string] => [
        value + minX,
        formatTimeDuration(Math.round(value), false),
      ],
    ),
  };
  const yAxis = {
    min: minY,
    max: maxY,
    ticks: findGridLinesDistance(
      minY,
      maxY * meterToDistanceUnitFactor(unitType),
      clamp(3, Math.round(height / 50), 10),
    ).map((value: number): [number, string] => [
      value * distanceUnitToMeterFactor(unitType),
      `${Math.round(value)} ${distanceUnitByUnitType(unitType)}`,
    ]),
  };

  return (
    <SVGChart width={width} height={height} xAxis={xAxis} yAxis={yAxis}>
      {(bounds) => {
        const timeX = bounds.paddingLeft + ((time - minX) / (maxX - minX)) * bounds.innerWidth;
        return (
          <>
            {starterInfos
              .filter((starterInfo) => starterInfo.points.length > 0)
              .map((starterInfo) => (
                <FlowChartStarter
                  key={starterInfo.starter.id}
                  points={starterInfo.points}
                  starter={starterInfo.starter}
                  minX={minX}
                  maxX={maxX}
                  minY={minY}
                  maxY={maxY}
                  bounds={bounds}
                  time={time}
                  unitType={unitType}
                />
              ))}
            {time >= minX && time <= maxX && (
              <line
                x1={timeX}
                x2={timeX}
                y1={bounds.paddingTop}
                y2={bounds.paddingTop + bounds.innerHeight}
                stroke="#ffffff"
                strokeWidth={2}
              />
            )}
          </>
        );
      }}
    </SVGChart>
  );
}
